import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, Chip, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import CustomBackdrop from '../../../components/CustomBackdrop/CustomBackdrop';
import CustomFooter from '../../../components/Footer/CustomFooter';
import {
  useAddCoreKeywordMutation,
  useGetCompanyCoreKeywordsQuery,
  useRejectCoreKeywordMutation,
  useTransferXoviKeywordToCoreKeywordsMutation,
  useXoviKeywordsForCrawlResultQuery,
} from '../../../gql/generated/graphql';
import { useSnackbar } from '../../../providers/SnackbarProvider';

interface Props {
  companyId: number;
  crawlResultId: number;
}

const KeywordsStepV2 = ({ companyId, crawlResultId }: Props) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [additionalKeyword, setAdditionalKeyword] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState<number[]>([]);

  // Core keywords (left panel)
  const {
    data: coreData,
    loading: coreLoading,
    refetch: refetchCore,
  } = useGetCompanyCoreKeywordsQuery({ variables: { companyId } });
  const coreKeywords = coreData?.getCompanyCoreKeywords?.edges?.map(edge => edge?.node) || [];

  // XOVI keywords (right panel)
  const { data: xoviData, loading: xoviLoading } = useXoviKeywordsForCrawlResultQuery({
    variables: { crawlResultId },
  });
  const xoviKeywords = xoviData?.xoviKeywordsForCrawlResult || [];

  const [rejectCoreKeyword, { loading: rejectLoading }] = useRejectCoreKeywordMutation();
  const [addCoreKeyword, { loading: addLoading }] = useAddCoreKeywordMutation();
  const [transferKeyword, { loading: transferLoading }] = useTransferXoviKeywordToCoreKeywordsMutation();

  // Set of core keyword texts for dimming already-transferred XOVI keywords
  const coreKeywordTexts = useMemo(
    () => new Set(coreKeywords.map(k => k?.keyword?.toLowerCase()).filter(Boolean)),
    [coreKeywords]
  );

  const handleDelete = async (row: any) => {
    const result = await rejectCoreKeyword({ variables: { keywordId: row.dbId } });
    if (result.data?.rejectCoreKeyword?.success) await refetchCore();
  };

  const handleDeleteSelected = async (ids: number[]) => {
    for (const id of ids) {
      await rejectCoreKeyword({ variables: { keywordId: id } });
    }
    await refetchCore();
    setSelectedKeywords([]);
  };

  const handleAddKeywords = async () => {
    const keywords = additionalKeyword
      .split(',')
      .map(k => k.trim())
      .filter(k => k);
    if (keywords.length === 0) return;
    for (const kw of keywords) {
      await addCoreKeyword({ variables: { companyId, keyword: kw } });
    }
    await refetchCore();
    setAdditionalKeyword('');
  };

  const handleTransfer = async (xoviKeywordId: number) => {
    const result = await transferKeyword({ variables: { companyId, xoviKeywordId } });
    if (result.data?.transferXoviKeywordToCoreKeywords?.success) {
      showSnackbar('Keyword transferred to core keywords', 'success');
      await refetchCore();
    } else {
      showSnackbar(result.data?.transferXoviKeywordToCoreKeywords?.message || 'Transfer failed', 'error');
    }
  };

  const coreColumns: GridColDef[] = [
    { field: 'keyword', headerName: 'Keyword', flex: 1, minWidth: 150 },
    { field: 'source', headerName: 'Source', width: 80 },
    {
      field: 'cpc',
      headerName: 'CPC',
      width: 80,
      valueFormatter: (value: number) => {
        if (value === -1) return 'n.a.';
        if (value === -2) return 'n/a';
        if (value == null) return '';
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
      },
    },
    { field: 'searchVolume', headerName: 'SEVO', width: 80 },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: params => (
        <IconButton size="small" onClick={() => handleDelete(params.row)}>
          <Tooltip title="Remove keyword">
            <DeleteOutlineIcon fontSize="small" />
          </Tooltip>
        </IconButton>
      ),
    },
  ];

  const xoviColumns: GridColDef[] = [
    { field: 'keyword', headerName: 'Keyword', flex: 1, minWidth: 150 },
    { field: 'cpc', headerName: 'CPC', width: 80 },
    { field: 'sevo', headerName: 'SEVO', width: 80 },
    { field: 'position', headerName: 'Pos', width: 60 },
    {
      field: 'transfer',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: params => {
        const alreadyTransferred = coreKeywordTexts.has(params.row.keyword?.toLowerCase());
        return alreadyTransferred ? (
          <Chip label="Added" size="small" color="success" variant="outlined" />
        ) : (
          <Tooltip title="Transfer to core keywords">
            <IconButton size="small" onClick={() => handleTransfer(params.row.id)} disabled={transferLoading}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left Panel: Core Keywords */}
        <Grid item xs={6}>
          <Typography variant="subtitle2" gutterBottom>
            Core Keywords
          </Typography>
          <DataGrid
            rows={coreKeywords}
            getRowId={row => row?.dbId}
            columns={coreColumns}
            loading={coreLoading}
            autoHeight
            disableRowSelectionOnClick
            rowHeight={35}
            checkboxSelection
            rowSelectionModel={selectedKeywords}
            onRowSelectionModelChange={(ids: number[]) => setSelectedKeywords(ids)}
            initialState={{
              sorting: { sortModel: [{ field: 'cpc', sort: 'desc' }] },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            slots={{
              footer: () => (
                <CustomFooter
                  onRefresh={() => refetchCore()}
                  onDeleteSelected={() => handleDeleteSelected(selectedKeywords)}
                />
              ),
            }}
            sx={{
              fontFamily: 'var(--font-data)',
              fontSize: 'var(--text-sm)',
              '& .MuiDataGrid-cell': { borderBottom: '1px solid var(--border-subtle)' },
            }}
          />
          <Box display="flex" gap={1} mt={1}>
            <TextField
              size="small"
              label="Add keywords (comma-separated)"
              value={additionalKeyword}
              onChange={e => setAdditionalKeyword(e.target.value)}
              fullWidth
            />
            <Button onClick={handleAddKeywords} variant="contained" size="small" disabled={addLoading}>
              Add
            </Button>
          </Box>
        </Grid>

        {/* Right Panel: XOVI Keywords */}
        <Grid item xs={6}>
          <Typography variant="subtitle2" gutterBottom>
            XOVI Keywords (Crawl Result)
          </Typography>
          <DataGrid
            rows={xoviKeywords}
            getRowId={row => row.id}
            columns={xoviColumns}
            loading={xoviLoading}
            autoHeight
            disableRowSelectionOnClick
            rowHeight={35}
            initialState={{
              sorting: { sortModel: [{ field: 'sevo', sort: 'desc' }] },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              fontFamily: 'var(--font-data)',
              fontSize: 'var(--text-sm)',
              '& .MuiDataGrid-cell': { borderBottom: '1px solid var(--border-subtle)' },
            }}
          />
        </Grid>
      </Grid>
      <CustomBackdrop open={rejectLoading || addLoading || transferLoading} />
    </Box>
  );
};

export default KeywordsStepV2;
